// Per-page <head> tags (title + description + OG + canonical/hreflang) via
// react-helmet-async. Page-only helper owned by Agent D.
//
// SEO i18n (polish-fix §4): emits a <link rel="canonical"> for the current locale
// URL and <link rel="alternate" hreflang> alternates (zh-Hans / en / x-default → zh)
// so search engines pick the right localized URL. URLs are absolute when a public
// base host is known (from /config.json publicHostname), otherwise relative paths
// are emitted (acceptable for v1 — search engines resolve them against the page URL).
//
// NOTE (out of scope): full per-route server-rendered <html lang> + pre-rendered
// canonical/hreflang in the initial HTML is a BFF/SSR concern (plan §6.4). This
// component only sets the CLIENT-side head; crawlers that execute JS will see it.
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { fetchRuntimeConfig } from "../api/client";
import { SUPPORTED_LOCALES, type AppLocale, htmlLang } from "../i18n";

interface PageMetaProps {
  title: string;
  description: string;
  lang: string;
}

/** Replace the leading /<locale> path segment with the given locale. */
function pathForLocale(pathname: string, locale: AppLocale): string {
  return pathname.replace(/^\/(zh|en)(?=\/|$)/, `/${locale}`);
}

export default function PageMeta({ title, description, lang }: PageMetaProps) {
  const location = useLocation();

  // Public base host (e.g. "https://www.example.com") from runtime config, used to
  // emit absolute canonical/alternate URLs. Falls back to relative when unknown.
  const [base, setBase] = useState<string>("");
  useEffect(() => {
    let active = true;
    void fetchRuntimeConfig().then((cfg) => {
      if (!active) return;
      const host = cfg?.publicHostname?.trim();
      if (!host) return;
      // Accept either a bare hostname or a full origin; normalize to an origin
      // with no trailing slash.
      const origin = /^https?:\/\//i.test(host) ? host : `https://${host}`;
      setBase(origin.replace(/\/+$/, ""));
    });
    return () => {
      active = false;
    };
  }, []);

  const fullUrl = (locale: AppLocale): string => {
    const path = `${pathForLocale(location.pathname, locale)}${location.search}`;
    return base ? `${base}${path}` : path;
  };

  const canonical = base
    ? `${base}${location.pathname}${location.search}`
    : `${location.pathname}${location.search}`;

  return (
    <Helmet>
      <html lang={htmlLang((SUPPORTED_LOCALES as readonly string[]).includes(lang) ? (lang as AppLocale) : "zh")} />
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical for the current locale URL. */}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates: one per supported locale + x-default → zh. */}
      <link rel="alternate" hrefLang="zh-Hans" href={fullUrl("zh")} />
      <link rel="alternate" hrefLang="en" href={fullUrl("en")} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl("zh")} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="/og.jpg" />
      <meta property="og:locale" content={lang === "zh" ? "zh_CN" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );
}
